import type { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

export interface IndexedLineString {
  index: number;
  lineString: LineStringFeature;
}

/**
 * Command for swapping some of a route's line strings for new geometry while its markers stay
 * where they are, as map-matching does. Stores the before and after of each changed line
 * string so one undo restores all of them.
 */
export class ReplaceLineStringsCommand implements UndoCommand {
  readonly type: string;
  readonly routeKey: string;

  private oldLineStrings: IndexedLineString[];
  private newLineStrings: IndexedLineString[];

  constructor(data: {
    type?: string;
    routeKey: string;
    oldLineStrings: IndexedLineString[];
    newLineStrings: IndexedLineString[];
  }) {
    this.type = data.type || 'REPLACE_LINE_STRINGS';
    this.routeKey = data.routeKey;
    this.oldLineStrings = JSON.parse(JSON.stringify(data.oldLineStrings));
    this.newLineStrings = JSON.parse(JSON.stringify(data.newLineStrings));
  }

  private apply(ctx: CommandContext, lineStrings: IndexedLineString[]): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    for (const { index, lineString } of lineStrings) {
      if (index >= 0 && index < route.lineStrings.length) {
        route.lineStrings[index] = JSON.parse(JSON.stringify(lineString));
      }
    }
    route.updated_at = new Date();
  }

  undo(ctx: CommandContext): void {
    this.apply(ctx, this.oldLineStrings);
  }

  redo(ctx: CommandContext): void {
    this.apply(ctx, this.newLineStrings);
  }
}
